import { useState } from "react";
import {
  IconX,
  IconMail,
  IconUserPlus,
  IconCheck,
  IconCopy,
  IconClock,
  IconAlertCircle
} from "@tabler/icons-react";
import { useInvitations, useCreateInvitation, useRevokeInvitation } from "../hooks/useInvitations";

interface InviteTeamModalProps {
  projectId: string;
  projectName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function InviteTeamModal({ projectId, projectName, isOpen, onClose }: InviteTeamModalProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"contributor" | "owner">("contributor");
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);

  const { data: invitations = [], isLoading } = useInvitations(projectId);
  const createInvitation = useCreateInvitation(projectId);
  const revokeInvitation = useRevokeInvitation(projectId);

  const pendingInvitations = invitations.filter((inv) => inv.status === "pending");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      const result = await createInvitation.mutateAsync({ email: email.trim(), role });
      setLastInviteUrl(result.inviteUrl);
      setEmail("");
    } catch (error) {
      console.error("Failed to create invitation:", error);
    }
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleRevoke = async (invitationId: string) => {
    try {
      await revokeInvitation.mutateAsync(invitationId);
    } catch (error) {
      console.error("Failed to revoke invitation:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <IconUserPlus className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Invite Team Members</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <IconX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {/* Invite Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <IconMail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contractor@example.com"
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as "contributor" | "owner")}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="contributor">Contributor (can only see their own work)</option>
                <option value="owner">Owner (full access to project)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Contributors are isolated - they can only see and edit their own submissions.
              </p>
            </div>

            <button
              type="submit"
              disabled={createInvitation.isPending || !email.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createInvitation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <IconUserPlus className="w-4 h-4" />
                  Send Invitation
                </>
              )}
            </button>
          </form>

          {/* Success Message with URL */}
          {lastInviteUrl && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
                <IconCheck className="w-4 h-4" />
                Invitation sent!
              </div>
              <p className="text-sm text-green-700 mb-2">
                Share this link with the invitee:
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={lastInviteUrl}
                  readOnly
                  className="flex-1 px-2 py-1 text-xs bg-white border rounded font-mono"
                />
                <button
                  onClick={() => handleCopyUrl(lastInviteUrl)}
                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
                >
                  {copiedUrl === lastInviteUrl ? (
                    <>
                      <IconCheck className="w-3 h-3" />
                      Copied
                    </>
                  ) : (
                    <>
                      <IconCopy className="w-3 h-3" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {createInvitation.isError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <IconAlertCircle className="w-4 h-4" />
                <span className="text-sm">
                  {(createInvitation.error as Error)?.message || "Failed to send invitation"}
                </span>
              </div>
            </div>
          )}

          {/* Pending Invitations */}
          {pendingInvitations.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Pending Invitations ({pendingInvitations.length})
              </h3>
              <div className="space-y-2">
                {pendingInvitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <IconMail className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{invitation.email}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="capitalize">{invitation.role}</span>
                          <span>·</span>
                          <IconClock className="w-3 h-3" />
                          <span>
                            Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(invitation.id)}
                      disabled={revokeInvitation.isPending}
                      className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && pendingInvitations.length === 0 && !lastInviteUrl && (
            <div className="mt-6 text-center text-gray-500 text-sm">
              <p>No pending invitations for {projectName}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
